import { MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { AssessmentInstanceDto, AssessmentPlayerMode, AssessmentQuestionDto, AssessmentSaveResultDto, AssessmentStartPlayerDto } from "generated/CoreApi_v1";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssPlayerBase } from "views/assessment/player/AssPlayerBase";

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    instanceId?: string | null;
    assessmentId: string | null;
    enrollmentId?: string | null;
    componentId?: string | null;
    deployId?: string | null;
    deployParticipantId?: string | null;
    preview?: boolean;
    minFinalScore?: number;
    forceReview?: boolean;
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const AssPlayerEvaluation: React.FC<IPros> = (props) => {
    const { instanceId, assessmentId, enrollmentId, componentId, deployId, deployParticipantId, preview, minFinalScore, forceReview, onClose } = props;

    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("assessment");

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [startPlayer, setStartPlayer] = useState<AssessmentStartPlayerDto>();

    useEffect(() => {
        async function getData() {
            try {
                if (preview) {
                    setStartPlayer({
                        deployId: null,
                        startDate: moment().toDate(),
                        endDate: moment().toDate(),
                        playerMode: AssessmentPlayerMode.NewInstance,
                        instanceId: null,
                        participantId: null,
                        message: null
                    });
                }
                else if (deployId) {
                    var result = await api.v1.assPlayer.getStartPlayerEvaluation({
                        assessmentId: assessmentId!,
                        deployId: deployId,
                        participantId: deployParticipantId!,
                    });
                    if (forceReview) setStartPlayer({ ...result.data, playerMode: AssessmentPlayerMode.FinalResult });
                    else setStartPlayer(result.data);
                } else {
                    setStartPlayer({
                        deployId: null,
                        startDate: moment().toDate(),
                        endDate: moment().toDate(),
                        playerMode: AssessmentPlayerMode.NewInstance,
                        instanceId: instanceId ?? null,
                        participantId: null,
                        message: null
                    });
                }
            } catch (e) {
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    if (!loading && startPlayer && startPlayer.playerMode == AssessmentPlayerMode.NoAccess) {
        enqueueSnackbar(startPlayer.message ?? t("AssPlayer.Evaluation.CannotAccessMessage"), { variant: "error" });
        return null;
    }

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => onClose(false)} />

            <MprcRequestIndicator type="request" visible={loading} />

            {
                !loading && startPlayer &&
                <AssPlayerBase
                    instanceId={startPlayer.instanceId}
                    startPlayerMode={startPlayer.playerMode}
                    deployId={deployId == null ? undefined : deployId}
                    createInstance={() =>
                        new Promise<AssessmentInstanceDto>((resolve, reject) => {
                            api.v1.assPlayer.createEvalInstance({
                                assessmentId: assessmentId!,
                                preview: preview ?? false,
                                minFinalScore: minFinalScore ?? null,
                                enrollmentId: enrollmentId ?? null,
                                componentId: componentId ?? null,
                                deployId: deployId ?? null,
                                deployParticipantId: deployParticipantId ?? null
                            })
                                .then(result => resolve(result.data))
                                .catch(e => reject(e));
                        })
                    }
                    startInstance={(instanceId) =>
                        new Promise<AssessmentSaveResultDto>((resolve, reject) => {
                            api.v1.assPlayer.startEvalInstance({
                                instanceId,
                                enrollmentId: enrollmentId ?? null,
                                componentId: componentId ?? null,
                                deployId: deployId ?? null,
                                deployParticipantId: deployParticipantId ?? null
                            })
                                .then(result => resolve(result.data))
                                .catch(e => reject(e));
                        })
                    }
                    getStartPlayer={(playerMode, instanceId) => {
                        if (instanceId) {
                            return new Promise<AssessmentInstanceDto>((resolve, reject) => {
                                api.v1.assPlayer.getEvalInstance({ instanceId })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            });
                        } else {
                            throw new Error('Instance is required!');
                        }
                    }}
                    getQuestion={(action, playerMode, instanceId, questionIdRef, versionRef) => {
                        if (instanceId) {
                            return new Promise<AssessmentQuestionDto>((resolve, reject) => {
                                api.v1.assPlayer.getQuestionAssInstance({
                                    instanceId: instanceId,
                                    questionIdRef: questionIdRef ?? null,
                                    versionRef: versionRef ?? null,
                                    playerMode,
                                    action
                                })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            });
                        } else {
                            throw new Error('Instance is required!');
                        }
                    }}
                    onClose={onClose}
                />
            }
        </>
    );
};
